import { FC, SyntheticEvent, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  OutlinedInput,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  createFilterOptions,
  autocompleteClasses,
  chipClasses,
  paperClasses,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Form, Formik } from "formik";
import Edit from "components/icons/Edit";
import { useAppSelector } from "utils/hooks/storeHooks";
import { createNewFilterRole, updateFilterRole } from "store/filterRole/thunks";
import { EditRoleSchema } from "modules/MyProfile/validators/editRoleSchema";
import variables from "../../../styles/partials/_variables.module.scss";
import { selectProvidersCollection } from "store/providers/selectors";
import { fetchAllProvidersCollection } from "store/providers/thunks";
import { FilterRole } from "types/FilterRole";
import { Clear } from "@mui/icons-material";

type RoleFormProps = {
  isNewRole: boolean;
  role: FilterRole;
};

const RoleForm: FC<RoleFormProps> = ({
  isNewRole = true,
  role,
}: RoleFormProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { roleId } = useParams();
  const providersCollection = useAppSelector(selectProvidersCollection);
  const providerId = "4";
  const [allowedTaxonomies, setAllowedTaxonomies] = useState("");
  const [isEditing, setIsEditing] = useState(isNewRole);
  const [roleName, setRoleName] = useState("");
  const [taxonomyCollection, setTaxonomyCollection] = useState<string[]>([]);
  const [roleDescription, setRoleDescription] = useState<string>("");
  const [hasAdminRights, setHasAdminRights] = useState<boolean>(false);
  const allTaxonomyOptions: string[] = providersCollection.map(
    (provider: any) => provider.title
  );

  let initialValues: any = {
    name: isNewRole ? "New Role" : role.name,
    description: isNewRole ? "--" : role.description,
    taxonomy: taxonomyCollection,
    isAdmin: role.isAdmin,
    // TODO: verify the format of the rest of permissions on the role's form
    // payer: [] as SelectOption[],
    // location: [] as SelectOption[],
    // contract: [] as SelectOption[],
  };
  const taxonomyFilterRolePermissions = role.permission.find(
    ({ providerId }: any) => providerId === Number(providerId)
  );
  const taxonomyFilters = taxonomyFilterRolePermissions?.filters?.find(
    ({ cardId }: any) => cardId === null
  );
  useEffect(() => {
    if (providerId) {
      dispatch(fetchAllProvidersCollection(providerId));
    }
    // Setting the states from in here to avoid some bugs
    if (
      taxonomyFilterRolePermissions &&
      taxonomyFilters?.collection &&
      !isNewRole
    ) {
      setTaxonomyCollection(taxonomyFilters.collection);
      setRoleName(isNewRole ? "New Role" : role.name);
      setRoleDescription(isNewRole ? "--" : role.description);
      setHasAdminRights(role.isAdmin);
    }
  }, [
    dispatch,
    isNewRole,
    providerId,
    role,
    role.permission,
    taxonomyFilterRolePermissions,
    taxonomyFilters,
  ]);

  //  ==================================  E D I T I N G  ==================================
  const onEditClick = () => {
    setIsEditing((isEditing) => !isEditing);
  };

  const handleAdminRights = () => {
    setHasAdminRights(!hasAdminRights);
  };

  const handleTextChange = (
    event: SyntheticEvent,
    isDesc?: boolean,
    isName?: boolean
  ) => {
    const { value } = event.target as HTMLTextAreaElement;
    if (isDesc) setRoleDescription(value);
    if (isName) setRoleName(value);
  };

  //  ==================================  S U B M I T I N G  ==================================

  const createNewRole = () => {
    dispatch(
      createNewFilterRole({
        name: roleName,
        description: roleDescription,
        // TODO: add the rest of selected permissions to the array array
        permission: [
          {
            providerId: 4,
            filters: [
              {
                cardId: null,
                collection: taxonomyCollection,
              },
            ],
          },
        ],
        isAdmin: hasAdminRights,
      })
    );
    navigate("/RolesManagement");
  };

  const saveChangesInCurrentRole = () => {
    dispatch(
      updateFilterRole(Number(roleId), {
        name: roleName,
        description: roleDescription,
        // TODO: add the rest of selected permissions to the array array
        permission: [
          {
            providerId: 4,
            filters: [
              {
                cardId: null,
                collection: taxonomyCollection,
              },
            ],
          },
        ],
        isAdmin: hasAdminRights,
      })
    );
  };

  const handleSubmit = () => {
    isNewRole ? createNewRole() : saveChangesInCurrentRole();
    onEditClick();
  };

  // filters up to 100 rendered options displayed on the autocomplete list
  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: any) => option,
    limit: 100,
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
  });

  useEffect(() => {
    setAllowedTaxonomies(taxonomyCollection.join(", "));
  }, [taxonomyCollection]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={EditRoleSchema}
      onSubmit={handleSubmit}
    >
      {/* TODO: add the message to display when errors !== undefined */}
      {({ isValid, errors }) => {
        const enableToSubmit = () => Boolean(isValid);

        return (
          <Form>
            <Box display="flex" justifyContent="space-between">
              <Box>
                <Typography color="#3D3D3D" fontSize="1.125rem" variant="body2">
                  Role Details
                </Typography>
                <Typography color="#707070" mb="1.5rem" variant="body2">
                  Name of your role and a quick description.{" "}
                </Typography>
              </Box>
              <Box
                display="flex"
                gap="10px"
                justifyContent="right"
                marginBottom="auto"
                width="200px"
              >
                {!isNewRole && (
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={onEditClick}
                  >
                    {isEditing ? "" : <Edit color={variables.mainBlue} />}
                    {isEditing ? "Cancel" : "Edit"}
                  </Button>
                )}
                {isEditing && !isNewRole && (
                  <Button
                    disabled={!enableToSubmit()}
                    type="submit"
                    variant="contained"
                  >
                    Save
                  </Button>
                )}
                {isNewRole && (
                  <Button
                    disabled={!enableToSubmit()}
                    fullWidth
                    type="submit"
                    variant="contained"
                  >
                    Save Role
                  </Button>
                )}
              </Box>
            </Box>
            {isEditing ? (
              <Box display="flex" flexDirection="column">
                <Box display="flex" flexDirection="column">
                  <Typography color={variables.tableGray} variant="caption">
                    Role Name
                  </Typography>
                  <OutlinedInput
                    defaultValue={isNewRole ? "" : role.name}
                    placeholder="Role name"
                    sx={{ width: 200 }}
                    onChange={(e) => handleTextChange(e, false, true)}
                  />{" "}
                </Box>
                <Box display="flex" flexDirection="column" mt="0.5rem">
                  <Typography color={variables.tableGray} variant="caption">
                    Description
                  </Typography>
                  <TextField
                    defaultValue={isNewRole ? "" : role.description}
                    inputProps={{ style: { padding: 0 } }}
                    multiline
                    placeholder="Description"
                    rows={3}
                    sx={{ width: "25rem" }}
                    onChange={(e) => handleTextChange(e, true, false)}
                  />{" "}
                </Box>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column">
                <Box display="flex" flexDirection="column">
                  <Typography color={variables.tableGray} variant="caption">
                    Role Name
                  </Typography>
                  <Typography variant="caption">{role.name}</Typography>
                </Box>
                <Box display="flex" flexDirection="column" mt="2.3rem">
                  <Typography color={variables.tableGray} variant="caption">
                    Description
                  </Typography>
                  <Typography variant="caption">{role.description}</Typography>
                </Box>
              </Box>
            )}
            <hr style={{ marginTop: "2rem" }} />
            <br />
            <Typography color="#3D3D3D" fontSize="1.125rem" variant="body2">
              Role Permission Details
            </Typography>
            <Typography color="#707070" mb="1.5rem" variant="body2">
              Select from the options the permissions for the role.
            </Typography>
            <Box display="flex" flexDirection="column" m="1rem 0">
              <Typography color={variables.tableGray} variant="caption">
                Taxonomy
              </Typography>
              {isEditing ? (
                <Autocomplete
                  autoComplete
                  ChipProps={{
                    deleteIcon: <Clear />,
                    sx: {
                      backgroundColor: variables.backgroundGray,
                      color: variables.darkBlue,
                      height: "1.5rem",
                      [`&.${autocompleteClasses.tag}`]: {
                        borderRadius: "8px",
                        margin: "0 .5rem 0 0",
                      },
                      [`& .${chipClasses.deleteIcon}`]: {
                        color: variables.darkBlue,
                      },
                    },
                  }}
                  componentsProps={{
                    paper: {
                      elevation: 3,
                      sx: {
                        [`&.${paperClasses.root}`]: { padding: 0, minWidth: 0 },
                      },
                    },
                  }} // shadow effect on the list
                  disableClearable
                  disableCloseOnSelect
                  filterOptions={filterOptions}
                  filterSelectedOptions
                  getOptionLabel={(option) => option}
                  limitTags={4}
                  multiple
                  noOptionsText="No taxonomies found."
                  onChange={(_, value) => {
                    setTaxonomyCollection(value);
                  }}
                  openOnFocus
                  options={allTaxonomyOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Taxonomies"
                      InputProps={{
                        ...params.InputProps,
                        type: "text",
                      }}
                    />
                  )}
                  sx={{ width: "100%" }}
                  value={taxonomyCollection}
                />
              ) : (
                <Typography variant="caption">
                  {allowedTaxonomies || "--"}
                </Typography>
              )}
            </Box>

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasAdminRights}
                    disabled={!isEditing}
                    size="small"
                    value={hasAdminRights}
                    onChange={handleAdminRights}
                  />
                }
                label="Has admin rights"
              />
              {/* TODO: enable the rest of checkboxes on edit once this backend part is ready */}
              <FormControlLabel
                control={<Checkbox disabled size="small" />}
                label="Payer"
              />
              <FormControlLabel
                control={<Checkbox disabled size="small" />}
                label="Location"
              />
              <FormControlLabel
                control={<Checkbox disabled size="small" />}
                label="Contract"
              />
            </FormGroup>
          </Form>
        );
      }}
    </Formik>
  );
};

export default RoleForm;
